import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@/store/settings.store'
import { showSaveSuccess, showSaveError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { fixedAssetService, FixedAssetVm, Lookup } from '@/services/fixedAssetService'
import { accountManagementService } from '@/services/accountManagementService'
import { financeService } from '@/services/financeService'
import type { Account } from '@/api/types'

// Validation schema
const fixedAssetSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  fixedAssetNumber: z.string().optional(),
  assetCategoryId: z.number().optional(),
  purchaseDate: z.string().optional(),
  purchasePrice: z.number().min(0, 'Purchase price must be 0 or greater').optional(),
  warrantyExpiry: z.string().optional(),
  serialNumber: z.string().optional(),
  costProfitCenterId: z.number().optional(),
  costLocationId: z.number().optional(),
  description: z.string().optional(),
  depreciationStartDate: z.string().optional(),
  depreciationMethod: z.union([z.string(), z.number()]).optional(),
  effectiveLifeYears: z.number().min(1, 'Effective life must be at least 1 year').optional(),
  fixedAssetAccountId: z.number().optional(),
  depreciationAccountId: z.number().optional(),
  expenseAccountId: z.number().optional(),
  yearlyRate: z.number().min(0).max(100, 'Yearly rate must be between 0 and 100').optional(),
  monthlyDepreciation: z.number().min(0).optional(),
  accumulatedDepreciation: z.number().min(0).optional(),
  notDepreciableValue: z.number().min(0).optional(),
  bookValue: z.number().min(0).optional(),
})

type FixedAssetFormData = z.infer<typeof fixedAssetSchema>

/**
 * Fixed Asset Edit/Create Page
 * Form for creating new fixed assets or editing existing ones
 */
export const FixedAssetsEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  
  const isEditing = Boolean(id)
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)

  // Lookup data
  const [assetCategories, setAssetCategories] = useState<Lookup[]>([])
  const [depreciationMethods, setDepreciationMethods] = useState<Lookup[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [costLocations, setCostLocations] = useState<any[]>([])
  const [profitCenters, setProfitCenters] = useState<any[]>([])

  // Effective Life Years options (1-20)
  const effectiveLifeYearsOptions = Array.from({ length: 20 }, (_, i) => i + 1).map(year => ({
    value: String(year),
    label: String(year),
  }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FixedAssetFormData>({
    resolver: zodResolver(fixedAssetSchema),
    defaultValues: {
      purchasePrice: 0,
      yearlyRate: 0,
      monthlyDepreciation: 0,
      accumulatedDepreciation: 0,
      notDepreciableValue: 0,
      bookValue: 0,
    },
  })

  // Watch values for calculations
  const purchasePrice = watch('purchasePrice') || 0
  const yearlyRate = watch('yearlyRate') || 0
  const effectiveLifeYears = watch('effectiveLifeYears') || 0
  const notDepreciableValue = watch('notDepreciableValue') || 0
  const accumulatedDepreciation = watch('accumulatedDepreciation') || 0
  const depreciationMethod = watch('depreciationMethod')
  
  // Check if Straight Line is selected
  const isStraightLine = useMemo(() => {
    if (!depreciationMethod) return false
    const methodId = typeof depreciationMethod === 'string' ? Number(depreciationMethod) : depreciationMethod
    const selectedMethod = depreciationMethods.find(m => m.id === methodId)
    if (!selectedMethod) return false
    const methodName = language === 'ar' 
      ? (selectedMethod.lookupNameAr || selectedMethod.lookupNameEn || '')
      : (selectedMethod.lookupNameEn || selectedMethod.lookupNameAr || '')
    return methodName.toLowerCase().includes('straight') && methodName.toLowerCase().includes('line')
  }, [depreciationMethod, depreciationMethods, language])

  // Calculate monthly depreciation and book value
  useEffect(() => {
    // For Straight Line method, calculate based on effective life years
    if (isStraightLine && purchasePrice > 0 && effectiveLifeYears > 0) {
      const yearlyDepreciation = purchasePrice / effectiveLifeYears
      const monthly = yearlyDepreciation / 12
      setValue('monthlyDepreciation', Number(monthly.toFixed(4)))
    } else if (!isStraightLine && purchasePrice > 0 && yearlyRate > 0) {
      // For other methods, use yearly rate
      const yearlyDepreciation = (purchasePrice * yearlyRate) / 100
      const monthly = yearlyDepreciation / 12
      setValue('monthlyDepreciation', Number(monthly.toFixed(4)))
    } else {
      setValue('monthlyDepreciation', 0)
    }
  }, [purchasePrice, yearlyRate, effectiveLifeYears, isStraightLine, setValue])

  useEffect(() => {
    if (purchasePrice > 0) {
      const bookVal = purchasePrice - accumulatedDepreciation - notDepreciableValue
      setValue('bookValue', Math.max(0, bookVal))
    } else {
      setValue('bookValue', 0)
    }
  }, [purchasePrice, accumulatedDepreciation, notDepreciableValue, setValue])

  // Load lookup data
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        const [
          categoriesData,
          depMethodsData,
          accountsData,
          costLocationsData,
          profitCentersData,
        ] = await Promise.all([
          fixedAssetService.getAssetCategoryLookups(),
          fixedAssetService.getDepreciationMethodLookups(),
          accountManagementService.getAllAccounts(branchId),
          financeService.getCostLocations(branchId),
          financeService.getProfitCenters(branchId),
        ])

        setAssetCategories(categoriesData || [])
        setDepreciationMethods(depMethodsData || [])
        setAccounts(accountsData || [])
        setCostLocations(costLocationsData || [])
        setProfitCenters(profitCentersData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        showSaveError(error as Error, false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load asset data for editing
  useEffect(() => {
    if (isEditing && id && branchId) {
      const loadAsset = async () => {
        try {
          setLoading(true)
          const asset = await fixedAssetService.getAssetById(Number(id), branchId)
          
          // Map API fields to form fields
          if (asset.name) setValue('name', asset.name)
          if (asset.fixedAssetNumber) setValue('fixedAssetNumber', asset.fixedAssetNumber)
          if (asset.assetCategoryId) setValue('assetCategoryId', asset.assetCategoryId)
          if (asset.purchaseDate) setValue('purchaseDate', asset.purchaseDate.split('T')[0])
          if (asset.purchasePrice !== undefined) setValue('purchasePrice', asset.purchasePrice)
          if (asset.warrantyExpiry) setValue('warrantyExpiry', asset.warrantyExpiry.split('T')[0])
          if (asset.serialNumber) setValue('serialNumber', asset.serialNumber)
          if (asset.costProfitCenterId) setValue('costProfitCenterId', asset.costProfitCenterId)
          if (asset.costLocationId) setValue('costLocationId', asset.costLocationId)
          if (asset.description) setValue('description', asset.description)
          if (asset.depreciationStartDate) setValue('depreciationStartDate', asset.depreciationStartDate.split('T')[0])
          if (asset.depreciationMethod) setValue('depreciationMethod', asset.depreciationMethod)
          if (asset.effectiveLifeYears) setValue('effectiveLifeYears', asset.effectiveLifeYears)
          if (asset.fixedAssetAccountId) setValue('fixedAssetAccountId', asset.fixedAssetAccountId)
          if (asset.depreciationAccountId) setValue('depreciationAccountId', asset.depreciationAccountId)
          if (asset.expenseAccountId) setValue('expenseAccountId', asset.expenseAccountId)
          if (asset.yearlyRate !== undefined) setValue('yearlyRate', asset.yearlyRate)
          if (asset.monthlyDepreciation !== undefined) setValue('monthlyDepreciation', asset.monthlyDepreciation)
          if (asset.accumulatedDepreciation !== undefined) setValue('accumulatedDepreciation', asset.accumulatedDepreciation)
          if (asset.notDepreciableValue !== undefined) setValue('notDepreciableValue', asset.notDepreciableValue)
          if (asset.bookValue !== undefined) setValue('bookValue', asset.bookValue)
        } catch (error: any) {
          console.error('Error loading asset:', error)
          await showSaveError(error, false)
          navigate('/fixed-assets')
        } finally {
          setLoading(false)
        }
      }

      loadAsset()
    }
  }, [isEditing, id, branchId, setValue, navigate])

  const onSubmit = async (data: FixedAssetFormData) => {
    if (!branchId) {
      await showSaveError(new Error('Branch not selected'), false)
      return
    }

    try {
      setLoading(true)

      // Helper function to convert date to ISO format (only if value exists)
      const toApiDate = (value?: string): string | undefined => {
        if (!value || !value.trim()) return undefined
        try {
          if (value.includes('T')) {
            return new Date(value).toISOString()
          }
          return new Date(`${value}T00:00:00`).toISOString()
        } catch {
          return undefined
        }
      }

      // Prepare asset data for API - Match exact API structure
      const assetData: FixedAssetVm = {
        fixedAssetId: isEditing && id ? Number(id) : 0,
        // Only include fixedAssetNo for edit mode (auto-generated for new assets)
        fixedAssetNo: isEditing && data.fixedAssetNumber && data.fixedAssetNumber.trim() ? data.fixedAssetNumber.trim() : '',
        fixedAssetEn: data.name || '', // Required field - English name
        fixedAssetAr: '', // Arabic name (can be added later if needed)
        purchaseDate: data.purchaseDate && data.purchaseDate.trim() ? toApiDate(data.purchaseDate) : undefined,
        purchasePrice: data.purchasePrice !== undefined && data.purchasePrice !== null ? data.purchasePrice : 0,
        warrantyExpiry: data.warrantyExpiry && data.warrantyExpiry.trim() ? toApiDate(data.warrantyExpiry) : undefined,
        serialNo: data.serialNumber && data.serialNumber.trim() ? data.serialNumber.trim() : '',
        assetTypeId: data.assetCategoryId && data.assetCategoryId > 0 ? data.assetCategoryId : 0,
        description: data.description && data.description.trim() ? data.description.trim() : '',
        location: data.costLocationId && data.costLocationId > 0 ? data.costLocationId : 0,
        costProfitCenterId: data.costProfitCenterId && data.costProfitCenterId > 0 ? data.costProfitCenterId : 0,
        qrCode: '',
        depreciationStartDate: data.depreciationStartDate && data.depreciationStartDate.trim() ? toApiDate(data.depreciationStartDate) : undefined,
        costLimit: 0,
        residualValue: 0,
        lastDepreciationRun: '',
        depreciationMethodId: data.depreciationMethod ? Number(data.depreciationMethod) || 0 : 0,
        monthlyDepreciation: data.monthlyDepreciation !== undefined && data.monthlyDepreciation !== null ? data.monthlyDepreciation : 0,
        notDepreciableValue: data.notDepreciableValue !== undefined && data.notDepreciableValue !== null ? data.notDepreciableValue : 0,
        accumulatedDepreciation: data.accumulatedDepreciation !== undefined && data.accumulatedDepreciation !== null ? data.accumulatedDepreciation : 0,
        rate: data.yearlyRate !== undefined && data.yearlyRate !== null ? data.yearlyRate : 0,
        fixedAssetAccount: data.fixedAssetAccountId && data.fixedAssetAccountId > 0 ? data.fixedAssetAccountId : 0,
        depreciationAccount: data.depreciationAccountId && data.depreciationAccountId > 0 ? data.depreciationAccountId : 0,
        expenseAccount: data.expenseAccountId && data.expenseAccountId > 0 ? data.expenseAccountId : 0,
        salesAccount: 0,
        gainLossAccount: 0,
        bookValue: data.bookValue !== undefined && data.bookValue !== null ? data.bookValue : 0,
        isSold: false,
        sellingValue: 0,
        sellingDate: undefined,
        effectiveLifeYears: data.effectiveLifeYears && data.effectiveLifeYears > 0 ? data.effectiveLifeYears : 0,
        companyId: user?.userId || 0,
        branchId: branchId || 0,
        portalId: 0,
        createdDate: undefined,
        createdBy: 0,
        modifiedDate: undefined,
        modifiedBy: 0,
      }

      // Log the payload being sent
      console.log('🔵 Fixed Asset Save Payload:', JSON.stringify(assetData, null, 2))
      console.log('🔵 Mode:', isEditing ? 'UPDATE' : 'CREATE')

      let response
      if (isEditing) {
        response = await fixedAssetService.updateAsset(assetData)
      } else {
        response = await fixedAssetService.saveAsset(assetData)
      }

      // Handle response: API returns 200 OK on success
      if (response && (response as any).success !== false) {
        await showSaveSuccess(isEditing, isEditing ? t('fixedAssets.messages.assetUpdatedSuccess') : t('fixedAssets.messages.assetSavedSuccess'))
        navigate('/fixed-assets')
      } else {
        await showSaveError(new Error((response as any)?.message || t('fixedAssets.messages.failedToSaveAsset')), isEditing)
      }
    } catch (error: any) {
      console.error('❌ Error saving fixed asset:', error)
      console.error('❌ Error response:', error.response?.data)
      console.error('❌ Error message:', error.response?.data?.message || error.message)
      
      // Show detailed error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save fixed asset'
      await showSaveError(new Error(errorMessage), isEditing)
    } finally {
      setLoading(false)
    }
  }

  // Prepare options for dropdowns
  const assetCategoryOptions = assetCategories.map((cat) => ({
    value: String(cat.id),
    label: language === 'ar' ? (cat.lookupNameAr || cat.lookupNameEn || '') : (cat.lookupNameEn || cat.lookupNameAr || ''),
  }))

  const depreciationMethodOptions = depreciationMethods.map((method) => ({
    value: String(method.id), // Use ID instead of name for API
    label: language === 'ar' ? (method.lookupNameAr || method.lookupNameEn || '') : (method.lookupNameEn || method.lookupNameAr || ''),
  }))

  const accountOptions = accounts.map((acc) => ({
    value: String(acc.id || acc.accountId || 0),
    label: `${acc.accountCode || ''} - ${language === 'ar' ? (acc.accountNameAr || acc.accountNameEn || '') : (acc.accountNameEn || acc.accountName || '')}`,
  }))

  const costLocationOptions = costLocations.map((cl) => ({
    value: String(cl.id || 0),
    label: cl.lookupNameEn || cl.nameEn || cl.name || '',
  }))

  const profitCenterOptions = profitCenters.map((pc) => ({
    value: String(pc.id || 0),
    label: pc.lookupNameEn || pc.nameEn || pc.name || '',
  }))

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditing ? t('fixedAssets.editFixedAsset') : t('fixedAssets.addNewFixedAsset')}
        description={isEditing ? t('fixedAssets.updateFixedAssetInformation') : t('fixedAssets.addNewFixedAssetToOrganization')}
      >
        <Button variant="outline" onClick={() => navigate('/fixed-assets')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('fixedAssets.return')}
        </Button>
      </PageHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Fixed Asset Info Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('fixedAssets.sections.fixedAssetInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="name">
                    {t('fixedAssets.fields.name')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    {...register('name')}
                    className={errors.name ? 'border-destructive' : ''}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                  )}
                </div>
                {isEditing && (
                  <div>
                    <Label htmlFor="fixedAssetNumber">{t('fixedAssets.fields.fixedAssetNumber')}</Label>
                    <Input id="fixedAssetNumber" {...register('fixedAssetNumber')} readOnly />
                  </div>
                )}
                <div>
                  <Label htmlFor="assetCategoryId">{t('fixedAssets.fields.assetCategory')}</Label>
                  <SearchableSelect
                    options={assetCategoryOptions}
                    value={watch('assetCategoryId')?.toString() || ''}
                    onValueChange={(value) => setValue('assetCategoryId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectAssetCategory')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchCategory')}
                    emptyMessage={t('fixedAssets.placeholders.noCategoryFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="purchaseDate">{t('fixedAssets.fields.purchaseDate')}</Label>
                  <Input
                    id="purchaseDate"
                    type="date"
                    {...register('purchaseDate')}
                  />
                </div>
                <div>
                  <Label htmlFor="purchasePrice">{t('fixedAssets.fields.purchasePrice')}</Label>
                  <Input
                    id="purchasePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('purchasePrice', { valueAsNumber: true })}
                    className={errors.purchasePrice ? 'border-destructive' : ''}
                  />
                  {errors.purchasePrice && (
                    <p className="text-sm text-destructive mt-1">{errors.purchasePrice.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="warrantyExpiry">{t('fixedAssets.fields.warrantyExpiry')}</Label>
                  <Input
                    id="warrantyExpiry"
                    type="date"
                    {...register('warrantyExpiry')}
                  />
                </div>
                <div>
                  <Label htmlFor="serialNumber">{t('fixedAssets.fields.serialNumber')}</Label>
                  <Input id="serialNumber" {...register('serialNumber')} />
                </div>
                <div>
                  <Label htmlFor="costProfitCenterId">{t('fixedAssets.fields.costProfitCenter')}</Label>
                  <SearchableSelect
                    options={profitCenterOptions}
                    value={watch('costProfitCenterId')?.toString() || ''}
                    onValueChange={(value) => setValue('costProfitCenterId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectCostProfitCenter')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchCenter')}
                    emptyMessage={t('fixedAssets.placeholders.noCenterFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="costLocationId">{t('fixedAssets.fields.costLocation')}</Label>
                  <SearchableSelect
                    options={costLocationOptions}
                    value={watch('costLocationId')?.toString() || ''}
                    onValueChange={(value) => setValue('costLocationId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectCostLocation')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchLocation')}
                    emptyMessage={t('fixedAssets.placeholders.noLocationFound')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('fixedAssets.sections.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="description"
                {...register('description')}
                rows={4}
                placeholder={t('fixedAssets.placeholders.enterAssetDescription')}
              />
            </CardContent>
          </Card>

          {/* Book Value Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('fixedAssets.sections.bookValue')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="depreciationStartDate">{t('fixedAssets.fields.depreciationStartDate')}</Label>
                  <Input
                    id="depreciationStartDate"
                    type="date"
                    {...register('depreciationStartDate')}
                  />
                </div>
                <div>
                  <Label htmlFor="depreciationMethod">{t('fixedAssets.fields.depreciationMethod')}</Label>
                  <SearchableSelect
                    options={depreciationMethodOptions}
                    value={watch('depreciationMethod')?.toString() || ''}
                    onValueChange={(value) => setValue('depreciationMethod', value)}
                    placeholder={t('fixedAssets.placeholders.selectDepreciationMethod')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchMethod')}
                    emptyMessage={t('fixedAssets.placeholders.noMethodFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="effectiveLifeYears">{t('fixedAssets.fields.effectiveLifeYears')}</Label>
                  <SearchableSelect
                    options={effectiveLifeYearsOptions}
                    value={watch('effectiveLifeYears')?.toString() || ''}
                    onValueChange={(value) => setValue('effectiveLifeYears', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectYears')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchYears')}
                    emptyMessage={t('fixedAssets.placeholders.noYearsFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="fixedAssetAccountId">{t('fixedAssets.fields.fixedAssetAccount')}</Label>
                  <SearchableSelect
                    options={accountOptions}
                    value={watch('fixedAssetAccountId')?.toString() || ''}
                    onValueChange={(value) => setValue('fixedAssetAccountId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectAccount')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchAccount')}
                    emptyMessage={t('fixedAssets.placeholders.noAccountFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="depreciationAccountId">{t('fixedAssets.fields.depreciationAccount')}</Label>
                  <SearchableSelect
                    options={accountOptions}
                    value={watch('depreciationAccountId')?.toString() || ''}
                    onValueChange={(value) => setValue('depreciationAccountId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectAccount')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchAccount')}
                    emptyMessage={t('fixedAssets.placeholders.noAccountFound')}
                  />
                </div>
                <div>
                  <Label htmlFor="expenseAccountId">{t('fixedAssets.fields.expenseAccount')}</Label>
                  <SearchableSelect
                    options={accountOptions}
                    value={watch('expenseAccountId')?.toString() || ''}
                    onValueChange={(value) => setValue('expenseAccountId', Number(value))}
                    placeholder={t('fixedAssets.placeholders.selectAccount')}
                    searchPlaceholder={t('fixedAssets.placeholders.searchAccount')}
                    emptyMessage={t('fixedAssets.placeholders.noAccountFound')}
                  />
                </div>
                {!isStraightLine && (
                  <div>
                    <Label htmlFor="yearlyRate">{t('fixedAssets.fields.yearlyRate')}</Label>
                    <Input
                      id="yearlyRate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('yearlyRate', { valueAsNumber: true })}
                      className={errors.yearlyRate ? 'border-destructive' : ''}
                    />
                    {errors.yearlyRate && (
                      <p className="text-sm text-destructive mt-1">{errors.yearlyRate.message}</p>
                    )}
                  </div>
                )}
                <div>
                  <Label htmlFor="monthlyDepreciation">{t('fixedAssets.fields.monthlyDepreciation')}</Label>
                  <Input
                    id="monthlyDepreciation"
                    type="number"
                    step="0.0001"
                    min="0"
                    {...register('monthlyDepreciation', { valueAsNumber: true })}
                    readOnly
                  />
                </div>
                <div>
                  <Label htmlFor="accumulatedDepreciation">{t('fixedAssets.fields.accumulatedDepreciation')}</Label>
                  <Input
                    id="accumulatedDepreciation"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('accumulatedDepreciation', { valueAsNumber: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="notDepreciableValue">{t('fixedAssets.fields.notDepreciableValue')}</Label>
                  <Input
                    id="notDepreciableValue"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('notDepreciableValue', { valueAsNumber: true })}
                  />
                </div>
                <div>
                  <Label htmlFor="bookValue">{t('fixedAssets.fields.bookValue')}</Label>
                  <Input
                    id="bookValue"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('bookValue', { valueAsNumber: true })}
                    readOnly
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/fixed-assets')} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? t('common.update') : t('fixedAssets.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

